import {
  Box,
  Tooltip,
  useDisclosure,
  Icon,
  IconButton,
} from '@chakra-ui/react';
import React, { useRef } from 'react';
import { FormBuilder } from '../../../FormBuilder';
import * as Yup from 'yup';
import { Popover } from '@/components/ats';
import { colors } from '@/theme/foundations/colors';
import { useKFlags } from '@/hooks/use-k-flags';
import { MessageQuestionCircle } from '@kula-ai/icons-core';
import { useSurveyList } from '@/hooks/useSurvey';

export const EditorSurvey = ({
  editor,
  color,
  isActive,
  onSurveySelect,
  onSurveyRemove,
}) => {
  const formRef = useRef(null);
  const { ats_enabled } = useKFlags();
  const popoverActions = useDisclosure();
  const { data: surveyOptions = [] } = useSurveyList();

  const handleOnInsert = (values, onClose) => {
    const { surveyType } = values || {};

    if (surveyType) {
      // Get current editor state
      const { state } = editor;
      const { doc, tr } = state;

      // Collect all survey node positions and their data (checking multiple possible node names)
      const nodesToDelete = [];
      doc.descendants((node, pos) => {
        // Check for various possible node type names
        if (
          node.type.name === 'survey' ||
          node.type.name === 'surveyComponent' ||
          node.type.name === 'Survey' ||
          node.type.name === 'surveySelect' ||
          (node.attrs && node.attrs.surveyId)
        ) {
          // Store the node data at collection time
          nodesToDelete.push({
            pos,
            size: node.nodeSize,
            surveyId: node.attrs?.surveyId,
          });
        }
      });

      // Delete nodes in reverse order to maintain correct positions
      if (nodesToDelete.length > 0) {
        // Call onSurveyRemove for each deleted survey
        if (onSurveyRemove) {
          nodesToDelete.forEach(({ surveyId }) => {
            if (surveyId) {
              const removedSurvey = surveyOptions?.find(
                (opt) => opt.value === surveyId
              );
              if (removedSurvey) {
                onSurveyRemove(removedSurvey);
              }
            }
          });
        }

        let transaction = tr;
        nodesToDelete.reverse().forEach(({ pos, size }) => {
          transaction = transaction.delete(pos, pos + size);
        });
        editor.view.dispatch(transaction);

        // Wait for deletion to complete before inserting new survey
        setTimeout(() => {
          editor?.chain().focus().insertSurvey({ surveyId: surveyType }).run();
        }, 50);
      } else {
        // No existing survey, insert immediately
        editor?.chain().focus().insertSurvey({ surveyId: surveyType }).run();
      }

      // Call the callback if provided
      if (onSurveySelect) {
        const selectedSurvey = surveyOptions?.find(
          (opt) => opt.value === surveyType
        );
        if (selectedSurvey) {
          onSurveySelect(selectedSurvey);
        }
      }
    }

    onClose?.();
  };

  const surveyConfig = [
    {
      name: 'surveyType',
      label: 'Survey',
      isRequired: true,
      type: 'kSelect',
      options: surveyOptions || [],
      validation: Yup.string().required('Survey type is required'),
      autoFocus: true,
      placeholder: 'Search surveys',
      customStyles: {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      },
    },
  ];

  const renderSurveyForm = (onClose = () => {}) => (
    <Box h="100%" w="100%" overflow="visible">
      <FormBuilder
        initialValues={{ surveyType: '' }}
        config={surveyConfig}
        onSubmit={(val) => handleOnInsert(val, onClose)}
        formRef={formRef}
        formContainerProps={{ m: 0 }}
      />
    </Box>
  );

  const triggerElement = (
    <Box width="32px" height="24px" display="flex" alignItems="center">
      <Tooltip
        label="Include Survey"
        variant={ats_enabled && 'primary'}
        arrowSize={8}
        hasArrow
        placement="top"
        borderRadius="4px"
      >
        <IconButton
          aria-label="Include Survey"
          height="auto"
          _hover={{ bg: 'inherit' }}
          borderRadius="6px"
          cursor="pointer"
          p="0"
          color={color}
          bg={isActive ? '##6D659D' : 'transparent'}
          data-test-id="editor-toolbar-survey-button"
        >
          <Icon
            as={MessageQuestionCircle}
            width="18px"
            height="18px"
            color={colors['secondary-700']}
          />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Popover
      popoverActions={popoverActions}
      triggerElement={triggerElement}
      popoverProps={{
        closeOnBlur: true,
        placement: 'top-end',
      }}
      ctaLabel="Insert"
      ctaOnClick={() => {
        if (formRef?.current) {
          formRef.current.submitForm();
        }
      }}
      popoverContentProps={{
        onClick: (e) => e.stopPropagation(),
        overflow: 'visible',
      }}
      popoverBodyProps={{
        overflow: 'visible',
        p: '0 16px',
        m: 0,
      }}
      popoverCloseButtonProps={{
        zIndex: 9999999,
        mt: '5px',
        mr: '5px',
      }}
    >
      {renderSurveyForm()}
    </Popover>
  );
};
